import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { cn } from '@apps/web/lib/utils';

export type CaregiverOutreachStats = {
    readonly selectedSignal: CaregiverOutreachSignal | null;
    readonly signalsByShift?: Array<CaregiverMatchSignal>;
    readonly forceExpandedShiftId?: string | null;
    // In a multi-shift outreach, always label each shift's signals so it is
    // clear which shift the caregiver was offered, even when they were only
    // contacted about a single shift.
    readonly alwaysShowShiftLabel?: boolean;
};

export type CaregiverMatchSignal = {
    readonly shiftId: string;
    readonly label: string;
    readonly weeklyHours: number | null;
    readonly previousVisits: number | null;
    readonly shiftHours?: number | null;
    readonly sameDayShiftCount: number | null;
    // Not rendered as a badge (deliberately removed as coordinator-facing
    // noise); still feeds the Match Signals sort as a tiebreaker.
    readonly declineCount: number | null;
    readonly isAvailable?: boolean;
    // This shift's client prefers this caregiver.
    readonly isPreferred?: boolean;
};

export type CaregiverOutreachSignal = {
    readonly weeklyHours: number | null;
    readonly previousVisits: number | null;
    readonly shiftHours?: number | null;
    readonly sameDayShiftCount: number | null;
    readonly declineCount: number | null;
};

const NEAR_OVERTIME_THRESHOLD_H = 35;
const OVERTIME_THRESHOLD_H = 40;

function formatHours(hours: number) {
    return Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
}

type SignalBadgesProps = {
    readonly weeklyHours: number | null;
    readonly previousVisits: number | null;
    readonly shiftHours?: number | null;
    readonly sameDayShiftCount: number | null;
    readonly isPreferred?: boolean;
    readonly maxTwoColumns?: boolean;
};

function SignalBadges({
    weeklyHours,
    previousVisits,
    shiftHours,
    sameDayShiftCount,
    isPreferred = false,
    maxTwoColumns = false,
}: SignalBadgesProps) {
    const hasWeeklyHours = weeklyHours !== null;
    const hasPreviousVisits = previousVisits !== null && previousVisits > 0;
    const hasSameDayShifts =
        sameDayShiftCount !== null && sameDayShiftCount > 0;

    if (
        !hasWeeklyHours &&
        !hasPreviousVisits &&
        !hasSameDayShifts &&
        !isPreferred
    ) {
        return null;
    }

    const weeklyHoursValue = weeklyHours ?? 0;
    const shiftHoursValue = shiftHours ?? null;
    const projectedWeeklyHours =
        weeklyHours !== null && shiftHoursValue !== null
            ? weeklyHoursValue + shiftHoursValue
            : null;
    const isNearOvertimeOrOvertime =
        weeklyHours !== null &&
        (weeklyHoursValue >= NEAR_OVERTIME_THRESHOLD_H ||
            (projectedWeeklyHours !== null &&
                projectedWeeklyHours > OVERTIME_THRESHOLD_H));
    const weeklyHoursVariantClass = isNearOvertimeOrOvertime
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-emerald-200 bg-emerald-50 text-emerald-700';
    const weeklyHoursText =
        isNearOvertimeOrOvertime && shiftHoursValue !== null
            ? `${formatHours(weeklyHoursValue)} scheduled hours + ${formatHours(shiftHoursValue)}`
            : `${formatHours(weeklyHoursValue)} scheduled hours`;
    const badgeClassName = maxTwoColumns
        ? 'w-fit justify-start text-left text-xs whitespace-nowrap'
        : 'w-fit text-xs';
    const preferredBadge = isPreferred ? (
        <Badge
            variant="outline"
            className={cn(
                badgeClassName,
                'border-emerald-200 bg-emerald-50 text-emerald-700',
            )}
        >
            <Star className="mr-1 h-3 w-3" />
            Preferred by client
        </Badge>
    ) : null;

    if (maxTwoColumns) {
        return (
            <div className="grid max-w-full min-w-0 grid-cols-[max-content_max-content] items-start gap-x-3 gap-y-2">
                {preferredBadge}
                {hasWeeklyHours && (
                    <Badge
                        variant="outline"
                        className={cn(badgeClassName, weeklyHoursVariantClass)}
                    >
                        {weeklyHoursText}
                    </Badge>
                )}
                {hasPreviousVisits && (
                    <Badge variant="outline" className={badgeClassName}>
                        Previous visits: {previousVisits}
                    </Badge>
                )}
                {hasSameDayShifts && (
                    <Badge variant="outline" className={badgeClassName}>
                        {sameDayShiftCount} shift
                        {sameDayShiftCount === 1 ? '' : 's'} on shift day
                    </Badge>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1">
            {preferredBadge}
            {hasWeeklyHours && (
                <Badge
                    variant="outline"
                    className={cn(badgeClassName, weeklyHoursVariantClass)}
                >
                    {weeklyHoursText}
                </Badge>
            )}
            {hasPreviousVisits && (
                <Badge variant="outline" className={badgeClassName}>
                    Previous visits: {previousVisits}
                </Badge>
            )}
            {hasSameDayShifts && (
                <Badge variant="outline" className={badgeClassName}>
                    {sameDayShiftCount} shift
                    {sameDayShiftCount === 1 ? '' : 's'} on shift day
                </Badge>
            )}
        </div>
    );
}

export function CaregiverOutreachStatsBadges({
    selectedSignal,
    signalsByShift,
    forceExpandedShiftId,
    alwaysShowShiftLabel = false,
}: CaregiverOutreachStats) {
    const useLabeledLayout =
        !!signalsByShift &&
        signalsByShift.length > 0 &&
        (signalsByShift.length > 1 || alwaysShowShiftLabel);
    const [expandedShiftIds, setExpandedShiftIds] = useState<Set<string>>(
        () =>
            new Set(
                (signalsByShift ?? [])
                    .filter((signal) => signal.isAvailable)
                    .map((signal) => signal.shiftId),
            ),
    );

    useEffect(() => {
        if (!signalsByShift || !useLabeledLayout) {
            setExpandedShiftIds(new Set());
            return;
        }

        if (
            forceExpandedShiftId &&
            signalsByShift.some(
                (signal) => signal.shiftId === forceExpandedShiftId,
            )
        ) {
            setExpandedShiftIds(new Set([forceExpandedShiftId]));
            return;
        }

        setExpandedShiftIds((previous) => {
            const next = new Set(
                signalsByShift
                    .filter((signal) => signal.isAvailable)
                    .map((signal) => signal.shiftId),
            );

            for (const shiftId of previous) {
                if (
                    signalsByShift.some((signal) => signal.shiftId === shiftId)
                ) {
                    next.add(shiftId);
                }
            }

            return next;
        });
    }, [forceExpandedShiftId, signalsByShift, useLabeledLayout]);

    if (signalsByShift && useLabeledLayout) {
        return (
            <div className="flex flex-col gap-2" data-row-click-ignore="true">
                {signalsByShift.map((signal) => {
                    const isExpanded = expandedShiftIds.has(signal.shiftId);

                    return (
                        <div
                            key={signal.shiftId}
                            className="flex flex-col gap-1"
                        >
                            <button
                                type="button"
                                className="text-foreground flex items-center gap-1 text-left text-xs font-medium hover:underline"
                                data-row-click-ignore="true"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setExpandedShiftIds((previous) => {
                                        const next = new Set(previous);
                                        if (next.has(signal.shiftId)) {
                                            next.delete(signal.shiftId);
                                        } else {
                                            next.add(signal.shiftId);
                                        }
                                        return next;
                                    });
                                }}
                            >
                                {isExpanded ? (
                                    <ChevronDown className="h-3 w-3" />
                                ) : (
                                    <ChevronRight className="h-3 w-3" />
                                )}
                                <span className="truncate">{signal.label}</span>
                            </button>
                            {isExpanded ? (
                                <div className="ml-4">
                                    <SignalBadges
                                        weeklyHours={signal.weeklyHours}
                                        previousVisits={signal.previousVisits}
                                        shiftHours={signal.shiftHours}
                                        sameDayShiftCount={
                                            signal.sameDayShiftCount
                                        }
                                        isPreferred={signal.isPreferred}
                                        maxTwoColumns={true}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    }

    const singleShiftSignal =
        signalsByShift && signalsByShift.length === 1
            ? signalsByShift[0]
            : null;

    return (
        <SignalBadges
            weeklyHours={
                selectedSignal?.weeklyHours ??
                singleShiftSignal?.weeklyHours ??
                null
            }
            previousVisits={
                selectedSignal?.previousVisits ??
                singleShiftSignal?.previousVisits ??
                null
            }
            shiftHours={
                selectedSignal?.shiftHours ??
                singleShiftSignal?.shiftHours ??
                null
            }
            sameDayShiftCount={
                selectedSignal?.sameDayShiftCount ??
                singleShiftSignal?.sameDayShiftCount ??
                null
            }
            isPreferred={singleShiftSignal?.isPreferred ?? false}
        />
    );
}
